import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNotificationStream } from "src/reusable/hooks/useNotificationStream";
import { nuclearn_api_url } from "src/App";
import { hasRevisionPrefix } from "src/helpers/revisionPrefix";
import { PermissionLevel } from "src/api/projectFolders/types";

import { recordHome, recordTitle } from "./types";
import type {
  RecordInfo,
  RelatedRecord,
  RevisionChainResponse,
  RevisionInfo,
} from "./types";

// === REVISION CHAIN ===
const getRecordRevisions = (
  sdrId: number,
): Promise<RevisionChainResponse | undefined> =>
  axios
    .get(`${nuclearn_api_url}/record-references/records/${sdrId}/revisions`)
    .then((res) => res.data)
    .catch((err) => {
      // Only a 404 means "this record has no chain to show". A 403, a 500 or a
      // dropped connection must stay an error: rendering them as an empty
      // chain would state, of a controlled document, that it has no superseded
      // revisions — a factual claim the failed request cannot support.
      if (err?.response?.status === 404) return undefined;
      throw err;
    });

/**
 * The record's revision chain, newest first. A record with no chain resolves
 * to a one-entry chain of itself, so consumers never special-case "no chain".
 * A 404 resolves to undefined and the revision UI simply doesn't render; every
 * other failure surfaces through `isError` for the caller to report.
 */
export const useRecordRevisions = (sdrId: number | null | undefined) =>
  useQuery(
    ["record-revisions", sdrId],
    () => getRecordRevisions(sdrId as number),
    {
      enabled: sdrId != null,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 60 * 1000,
    },
  );

// === PUBLISH NEW VERSION (dataset-homed records) ===
const uploadDatasetRecordVersion = ({
  datasetId,
  file,
  supersedesSdrId,
  revisionLabel,
  onProgress,
}: {
  datasetId: number;
  file: File;
  supersedesSdrId: number;
  revisionLabel?: string;
  onProgress?: (progress: number) => void;
}): Promise<unknown> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("supersedes_sdr_id", String(supersedesSdrId));
  if (revisionLabel != null) {
    formData.append("revision_label", revisionLabel);
  }
  return axios
    .put(`${nuclearn_api_url}/datasets/${datasetId}/upload-file`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total),
          );
        }
      },
    })
    .then((res) => res.data);
};

/**
 * Publish-new-version for a DATASET-homed record (Library spec §5, extended
 * to dataset containers): the upload ingests as a new SDR and is chained onto
 * the target on terminal processing success. Unlike a folder publish, no
 * references are repointed — referencing projects keep their pinned revision
 * and get the newer-revision notice instead.
 */
export const useUploadDatasetRecordVersion = () => {
  const queryClient = useQueryClient();
  return useMutation(uploadDatasetRecordVersion, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        "allSourceDataRecords",
        variables.datasetId,
      ]);
      // No revision-chain invalidation here: at upload time the publish has
      // not been linked into the chain yet. useDatasetRevisionChainSync busts
      // it when the SDR reaches a terminal state, which is when it changes.
    },
  });
};

/**
 * Busts the viewed record's revision chain when an SDR in `datasetId` reaches
 * a terminal processing state.
 *
 * A dataset-homed publish is linked into the chain on terminal success, not at
 * upload, so the upload is the wrong moment to refetch — it fires against
 * unchanged state and leaves the real transition uncovered. The folder path
 * gets this from useFolderProcessingStatus; datasets carry the same signal on
 * `notify:datasets:changed` with `action === "sdr_status_changed"`, which is
 * what already drives the dataset's own not-embedded count.
 *
 * `dataset_changed` is broadcast and throttled, so it carries no record or
 * chain ids; the viewed record's chain is the one this page can name.
 */
export const useDatasetRevisionChainSync = (
  datasetId: number | null | undefined,
  sdrId: number | null | undefined,
) => {
  const queryClient = useQueryClient();
  useNotificationStream(
    datasetId != null && sdrId != null ? "notify:datasets:changed" : null,
    "dataset_changed",
    (data) => {
      const ev = data as { action?: string; dataset_id?: number } | undefined;
      if (ev?.action === "sdr_status_changed" && ev?.dataset_id === datasetId) {
        queryClient.invalidateQueries(["record-revisions", sdrId]);
      }
    },
  );
};

/** The chain marks currency by omission: no superseded_date means current. */
export const isCurrentRevision = (rev: {
  superseded_date?: string | null;
}): boolean => rev.superseded_date == null;

/** The caller's write authority over a record's home container. */
export type HomeWriteAccess =
  | { kind: "folder"; permissionLevel: PermissionLevel | null | undefined }
  | { kind: "dataset"; canWrite: boolean };

/**
 * Whether "Upload new version" may be offered: write on the record's home and
 * the record is known to be current. An unresolved record is not publishable.
 */
export const canPublishNewVersion = (
  access: HomeWriteAccess,
  record: { superseded_date?: string | null } | null | undefined,
): boolean => {
  const canWrite =
    access.kind === "folder"
      ? access.permissionLevel === PermissionLevel.Owner ||
        access.permissionLevel === PermissionLevel.Write
      : access.canWrite;
  return canWrite && record != null && isCurrentRevision(record);
};

/**
 * Returns the label prefixed with "Rev" if it isn't already
 * (`hasRevisionPrefix`, shared with the chat/citation surfaces' own "Rev. "
 * convention — see src/helpers/revisionPrefix.ts).
 */
export const revisionLabelText = (label: string): string =>
  hasRevisionPrefix(label) ? label : `Rev ${label}`;

/** Display label for a revision ("Rev 2" or an already-prefixed label, else create-date/id fallback). */
export const revisionDisplayLabel = (rev: RevisionInfo): string => {
  if (rev.revision_label) return revisionLabelText(rev.revision_label);
  if (rev.create_date) {
    const d = new Date(rev.create_date);
    // An unparsable date would otherwise render the literal "Invalid Date".
    if (!Number.isNaN(d.getTime()))
      return `Revision of ${d.toLocaleDateString()}`;
  }
  return `Revision ${rev.id}`;
};

// === RELATED DOCUMENTS ===

/**
 * Related documents come from the platform hybrid search (`POST /search/`,
 * the RRF pipeline global search uses) queried with the record's title —
 * there is no dedicated relatedness endpoint yet. Rows are deduped to one
 * entry per SDR; tabular rows (no file_name) are dropped.
 */
const searchRelatedRecords = async (
  query: string,
): Promise<RelatedRecord[]> => {
  const res = await axios.post(
    `${nuclearn_api_url}/search/`,
    { search_query: query },
    { params: { with_sources: true, n_results: 15 } },
  );
  const rows: Array<[unknown, Record<string, any> | null | undefined]> =
    Array.isArray(res.data) ? res.data : [];
  const seen = new Set<number>();
  const out: RelatedRecord[] = [];
  for (const [, sdr] of rows) {
    if (!sdr || sdr.id == null || seen.has(sdr.id)) continue;
    seen.add(sdr.id);
    const fileName: string | null = sdr.data?.file_name ?? null;
    if (!fileName) continue;
    out.push({
      // recordTitle rather than a second inline copy: it trims and type-checks,
      // so a non-string title cannot reach <ListItemText primary> and throw
      // "Objects are not valid as a React child".
      title: recordTitle(sdr as RecordInfo) ?? fileName,
      sdrId: sdr.id,
      fileName,
      home: recordHome(sdr as RecordInfo),
    });
  }
  return out;
};

/** Documents similar to `query` (unfiltered — callers exclude the chain). */
export const useRelatedRecords = (query: string | null | undefined) =>
  useQuery(
    ["record-related", query],
    () => searchRelatedRecords(query as string),
    {
      enabled: !!query,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  );
